import {
  freezeWalletService,
  getWalletByIdService,
  getWalletsService,
  getWalletStatsService,
  unfreezeWalletService,
} from "@/services/wallets.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const WALLETS_KEY = ["wallets"] as const;
const WALLETS_STATS_KEY = ["wallets", "stats"] as const;

interface WalletFilters {
  page?: number;
  limit?: number;
  userId?: string;
  currency?: string;
  status?: string;
  search?: string;
}

export const useWallets = (filters: WalletFilters = {}) =>
  useQuery({
    queryKey: [...WALLETS_KEY, filters],
    queryFn: async () => {
      const {data} = await getWalletsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useWallet = (id: string) =>
  useQuery({
    queryKey: [...WALLETS_KEY, id],
    queryFn: async () => {
      const {data} = await getWalletByIdService(id);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!id,
  });

export const useWalletStats = () =>
  useQuery({
    queryKey: WALLETS_STATS_KEY,
    queryFn: async () => {
      const {data} = await getWalletStatsService();
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useFreezeWallet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => freezeWalletService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: WALLETS_KEY});
    },
  });
};

export const useUnfreezeWallet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unfreezeWalletService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: WALLETS_KEY});
    },
  });
};
